import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Configurar worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const PDFViewer = ({ url, onError }) => {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPDF();
    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [url]);

  useEffect(() => {
    if (pdfDoc) {
      renderPage(pageNum);
    }
  }, [pageNum, pdfDoc]);

  const loadPDF = async () => {
    setLoading(true);
    try {
      console.log('📥 Cargando PDF desde:', url);
      
      // Configurar opciones de carga
      const loadingTask = pdfjsLib.getDocument({
        url: url,
        withCredentials: false,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
        cMapPacked: true,
      });
      
      const pdf = await loadingTask.promise;
      
      console.log('✅ PDF cargado exitosamente, páginas:', pdf.numPages);
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('❌ Error cargando PDF:', error);
      console.error('URL intentada:', url);
      console.error('Tipo de error:', error.name);
      console.error('Mensaje:', error.message);
      setLoading(false);
      setError(error.message || 'Error desconocido');
      if (onError) onError(error);
    }
  };

  const renderPage = async (num) => {
    if (!pdfDoc || !canvasRef.current) return;
    
    setRendering(true);
    try {
      const page = await pdfDoc.getPage(num);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Para documentos portrait, calcular escala para que quepa completo
      // Modal es 80vw de ancho, usar 95% de eso para el canvas
      const availableWidth = Math.min(window.innerWidth * 0.80 * 0.95, 950);
      const availableHeight = window.innerHeight * 0.95 - 200; // Restar header y controles
      
      // Obtener dimensiones originales
      const unscaledViewport = page.getViewport({ scale: 1, rotation: 0 });
      
      // Calcular escala basada en el menor de los dos (ancho o alto)
      const scaleByWidth = availableWidth / unscaledViewport.width;
      const scaleByHeight = availableHeight / unscaledViewport.height;
      const scale = Math.min(scaleByWidth, scaleByHeight, 2); // Máximo 2x para no pixelar
      
      // Aplicar escala
      const viewport = page.getViewport({ scale, rotation: 0 });
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      console.log(`✅ Página ${num} renderizada - Escala: ${scale.toFixed(2)}x (${viewport.width.toFixed(0)}x${viewport.height.toFixed(0)}px)`);
    } catch (error) {
      console.error('Error renderizando página:', error);
    } finally {
      setRendering(false);
    }
  };

  const previousPage = () => {
    if (pageNum <= 1) return;
    setPageNum(pageNum - 1);
  };

  const nextPage = () => {
    if (pageNum >= numPages) return;
    setPageNum(pageNum + 1);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '60px',
        minHeight: '400px'
      }}>
        <RefreshCw size={48} className="spinning" style={{ color: '#fbb040', marginBottom: '24px' }} />
        <p style={{ color: '#9ca3af', fontSize: '16px', margin: 0 }}>Cargando PDF...</p>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>Descargando documento para visualización</p>
      </div>
    );
  }

  if (!pdfDoc || error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
        <p style={{ fontSize: '16px', marginBottom: '8px' }}>Error al cargar el PDF</p>
        {error && <p style={{ fontSize: '13px', color: '#9ca3af' }}>{error}</p>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
      {/* Canvas para renderizar PDF */}
      <div style={{ 
        background: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        maxWidth: '100%',
        overflow: 'auto'
      }}>
        <canvas 
          ref={canvasRef}
          style={{ 
            display: 'block',
            maxWidth: '100%',
            height: 'auto'
          }}
        />
      </div>

      {/* Controles de navegación */}
      {numPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          background: 'rgba(31, 41, 55, 0.9)',
          padding: '12px 24px',
          borderRadius: '12px',
          border: '1px solid rgba(75, 85, 99, 0.5)'
        }}>
          <button
            onClick={previousPage}
            disabled={pageNum <= 1 || rendering}
            style={{
              padding: '8px 16px',
              background: (pageNum <= 1 || rendering) ? 'rgba(107, 114, 128, 0.3)' : '#fbb040',
              border: 'none',
              borderRadius: '8px',
              color: (pageNum <= 1 || rendering) ? '#6b7280' : '#111827',
              cursor: (pageNum <= 1 || rendering) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            <ChevronLeft size={16} />
            Anterior
          </button>
          
          <span style={{ 
            color: 'white', 
            fontSize: '14px', 
            fontWeight: '600',
            minWidth: '120px',
            textAlign: 'center'
          }}>
            Página {pageNum} de {numPages}
          </span>
          
          <button
            onClick={nextPage}
            disabled={pageNum >= numPages || rendering}
            style={{
              padding: '8px 16px',
              background: (pageNum >= numPages || rendering) ? 'rgba(107, 114, 128, 0.3)' : '#fbb040',
              border: 'none',
              borderRadius: '8px',
              color: (pageNum >= numPages || rendering) ? '#6b7280' : '#111827',
              cursor: (pageNum >= numPages || rendering) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Siguiente
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      
      {rendering && (
        <p style={{ color: '#9ca3af', fontSize: '13px' }}>Renderizando página...</p>
      )}
    </div>
  );
};

export default PDFViewer;
